/**
 * Formatting for "where in the document" evidence locations. Heading paths come from the
 * ingestion parser, which falls back to leading paragraph text when a document has no real
 * heading — so a segment can be a whole paragraph long.
 */

/** Between heading segments. An arrow reads as "path into the document" at a glance. */
export const EVIDENCE_HEADING_SEPARATOR = " → ";

/**
 * A location is orientation, not content, and a deep section path is legitimately long: the
 * popover wraps it under the file name rather than clipping it to one line. The cap is what
 * stops an ingestion fallback that is really a whole paragraph from becoming the citation.
 */
export const MAX_EVIDENCE_LOCATION_CHARS = 160;

/**
 * Cuts overlong text at a word boundary near the cap and appends an ellipsis. The start is
 * kept: the page and top-level section are what a reader recognizes, and the deepest
 * segment is usually restated by the excerpt shown alongside.
 */
export function truncateEvidenceLocation(
  text: string,
  maxChars: number = MAX_EVIDENCE_LOCATION_CHARS
): string {
  if (text.length <= maxChars) return text;
  const slice = text.slice(0, maxChars);
  const lastSpace = slice.lastIndexOf(" ");
  // Back up to the previous word unless that would cost a disproportionate chunk.
  const cut = lastSpace > maxChars - 24 ? slice.slice(0, lastSpace) : slice;
  // A cut landing on a separator would dangle an arrow or middot before the ellipsis.
  return `${cut.replace(/[\s·→]+$/u, "")}…`;
}

export function formatEvidenceHeadingPath(
  headingPath: readonly string[] | undefined
): string | null {
  return headingPath?.filter(Boolean).join(EVIDENCE_HEADING_SEPARATOR) || null;
}

export function getEvidencePageLabel(
  pageStart: number | undefined,
  pageEnd: number | undefined
): string | null {
  if (pageStart === undefined) return null;
  if (pageEnd === undefined || pageEnd === pageStart) return `Page ${pageStart}`;
  return `Pages ${pageStart}–${pageEnd}`;
}

/**
 * `Page 3 · Strategy Evolution → Consumer data…` — page first, because it is the part a
 * reader recognizes without having opened the document; capped as one line of orientation.
 */
export function formatEvidenceLocation({
  pageStart,
  pageEnd,
  headingPath,
}: {
  pageStart?: number;
  pageEnd?: number;
  headingPath?: readonly string[];
}): string | null {
  const combined = [
    getEvidencePageLabel(pageStart, pageEnd),
    formatEvidenceHeadingPath(headingPath),
  ]
    .filter(Boolean)
    .join(" · ");
  return combined ? truncateEvidenceLocation(combined) : null;
}
